/**
 * The pages nobody wants to reach.
 *
 * They get real attention here for one reason: they are the pages a user sees
 * when they are already frustrated, and the difference between "403 Forbidden"
 * and a sentence explaining that an administrator can grant access is the
 * difference between a support ticket and a user who knows what to do next.
 *
 * Every screen says three things: what happened, why, and what to do about it.
 * The last one is the part usually missing.
 */

import { useTranslation } from "react-i18next";
import { Button, Card } from "@hardauto/core-ui";

import { NAMESPACE } from "../i18n";
import "./errors.css";

interface ErrorPageProps {
  title: string;
  body: string;
  /** Reference the user can quote to support. */
  reference?: string | undefined;
  actions?: readonly { label: string; href?: string; onClick?: () => void }[];
}

function ErrorPage({ title, body, reference, actions = [] }: ErrorPageProps) {
  return (
    <div className="hardauto-error-page">
      <Card>
        {/*
          A real `<h1>`, not styled text. This is the whole content of the
          page, and a screen-reader user landing here with no heading has no
          idea what has happened.
        */}
        <h1 className="hardauto-error-page__title">{title}</h1>
        <p className="hardauto-error-page__body">{body}</p>

        {reference ? (
          // Selectable and monospaced, because its only purpose is to be
          // copied into a support message.
          <p className="hardauto-error-page__reference">
            <code>{reference}</code>
          </p>
        ) : null}

        <div className="hardauto-error-page__actions">
          {actions.map((action) =>
            action.href ? (
              <a
                key={action.label}
                className="hardauto-button"
                data-variant="primary"
                href={action.href}
              >
                {action.label}
              </a>
            ) : (
              <Button key={action.label} variant="secondary" onClick={action.onClick}>
                {action.label}
              </Button>
            ),
          )}
        </div>
      </Card>
    </div>
  );
}

export function NotFoundScreen({ homeHref = "/" }: { homeHref?: string }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ErrorPage
      title={t("notFound.title")}
      body={t("notFound.body")}
      actions={[{ label: t("notFound.home"), href: homeHref }]}
    />
  );
}

/**
 * 403.
 *
 * Says explicitly that the account IS signed in and simply lacks permission,
 * because the most common wrong guess a user makes here is that they are
 * signed in as the wrong person — and the second is that the application is
 * broken. Offering "sign in as someone else" handles the first without
 * pretending it is the likely cause.
 */
export function ForbiddenScreen({
  homeHref = "/",
  signInHref,
}: {
  homeHref?: string;
  signInHref?: string;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ErrorPage
      title={t("forbidden.title")}
      body={t("forbidden.body")}
      actions={[
        { label: t("forbidden.home"), href: homeHref },
        ...(signInHref ? [{ label: t("forbidden.signInAs"), href: signInHref }] : []),
      ]}
    />
  );
}

export function UnauthorizedScreen({ signInHref = "/sign-in" }: { signInHref?: string }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ErrorPage
      title={t("unauthorized.title")}
      body={t("unauthorized.body")}
      actions={[{ label: t("unauthorized.signIn"), href: signInHref }]}
    />
  );
}

/**
 * 500.
 *
 * The correlation id is shown deliberately. It is the one thing that turns
 * "the site broke" into a report someone can act on — the same id is on the
 * server's log line and in the audit trail — and asking a user to describe
 * what they were doing gets a far worse answer than asking them to copy eight
 * characters.
 */
export function ServerErrorScreen({
  correlationId,
  onRetry,
  homeHref = "/",
}: {
  correlationId?: string | undefined;
  onRetry?: () => void;
  homeHref?: string;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ErrorPage
      title={t("serverError.title")}
      body={t("serverError.body")}
      reference={correlationId ? t("serverError.reference", { id: correlationId }) : undefined}
      actions={[
        ...(onRetry ? [{ label: t("serverError.retry"), onClick: onRetry }] : []),
        { label: t("serverError.home"), href: homeHref },
      ]}
    />
  );
}

export function OfflineScreen({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ErrorPage
      title={t("offline.title")}
      body={t("offline.body")}
      actions={onRetry ? [{ label: t("offline.retry"), onClick: onRetry }] : []}
    />
  );
}
